/**
 * WorkflowJobDetails - The "Job Details" panel inside the work order workflow.
 *
 * The workflow overlay is a full-screen takeover: once a tech taps Start Work
 * Order they see the checklist and nothing else, with the detail page hidden
 * behind it. That left no way to do three things the crew needs mid-job —
 * pull in the helpers who actually turned up, write down what happened, and
 * look up the address / contact / schedule they came for. This panel is that
 * missing surface, reachable from the overlay's header at every step.
 *
 * Three sections, ordered by what the tech opened the panel to DO:
 *   1. People On This Job — the crew, with add/remove. On an hour-tracked
 *      work order it becomes the hours editor instead, because the server
 *      will not accept a crew change without hours for everyone on it
 *      (WorkOrderController::assertHoursSupplied) — so the panel asks for
 *      them here rather than failing the save.
 *   2. Job Notes — free text on work_orders.checklist_notes, the same column
 *      the sign-off step writes. One set of notes for the job, not two.
 *   3. Job Info — read-only: customer, site (with a Directions link), on-site
 *      contact (tap-to-call / tap-to-email), schedule, priority, and the
 *      description the office wrote.
 *
 * Everything saves in one PATCH so a tech on a weak signal makes one request,
 * not three. The parent refetches on success via onSaved.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AutoGrowTextarea } from '../../atoms';
import { InfoRow, InfoSection } from '../../molecules';
import { AssigneeFieldset } from '../../molecules/AssigneeFieldset';
import { SectionCard } from '../../molecules/SectionCard';
import { readAdditionalAssigneeIds, readAssigneeHours, sameHours, sameIdSet } from '../../../utils/assigneeHours';
import { TmAssigneeHoursFieldset } from '../../molecules/TmAssigneeHoursFieldset';
import { workOrderService } from '../../../services/workOrderService';
import type { WorkOrder, UpdateWorkOrderData } from '../../../services/workOrderService';
import { getDirectionsUrlFromParts } from '../../../utils/directionsLink';
import { useAuth } from '../../../hooks/useAuth';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { formatDate, formatDateTime } from '../../../utils/formatDate';
import { formatLabel } from '../../../utils/formatLabel';
import { formatPhone } from '../../../utils/formatPhone';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface WorkflowJobDetailsProps {
    /** The work order being worked. Supplies both the read-only info and the
     *  starting values for the editable crew / notes. */
    workOrder: WorkOrder;
    /** Close the panel and drop back to the checklist. */
    onClose: () => void;
    /** Fired after a successful save so the parent can refetch the record. */
    onSaved?: () => void;
    /** Optional outer className merged onto the root element. */
    className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/** Shared label styling token — matches the form section molecules, which
 *  is what the two assignee fieldsets are built to sit inside. */
const LABEL_CLASS = 'block text-14px font-sans font-medium text-brand-text mb-6px';

/* ============================================
 * COMPONENT
 * ============================================ */

const WorkflowJobDetails: React.FC<WorkflowJobDetailsProps> = ({
    workOrder,
    onClose,
    onSaved,
    className,
}) => {
    /* Moving the job to someone else needs Can Reassign Work Orders — see
       the hours editor's primaryLocked below. */
    const { user } = useAuth();
    /* What the server currently holds. These are the baseline the editable
       state is seeded from AND the thing it's diffed against at save time —
       the panel sends only what the tech actually changed. */
    const storedAssigneeId = workOrder.assigneeId ?? '';
    const storedNotes = workOrder.checklistNotes ?? '';
    const storedAdditionalIds = useMemo(
        () => readAdditionalAssigneeIds(workOrder),
        [workOrder],
    );
    const storedHours = useMemo(() => readAssigneeHours(workOrder), [workOrder]);

    /* Editable copies of what the panel can write. Seeded from the record and
       re-seeded whenever a fresh one arrives (a save, or the parent's poll),
       so the panel never shows stale crew after someone else edits the job. */
    const [assigneeId, setAssigneeId] = useState<string>(storedAssigneeId);
    const [additionalAssigneeIds, setAdditionalAssigneeIds] = useState<string[]>(storedAdditionalIds);
    const [assigneeHours, setAssigneeHours] = useState<Record<string, string>>(storedHours);
    const [notes, setNotes] = useState<string>(storedNotes);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /* Flips true on a successful save and back to false on the next edit, so
       the tech gets a confirmation without a toast layer over the overlay. */
    const [saved, setSaved] = useState(false);

    /* Re-seed on a new server copy. Keyed on updatedAt as well as id: the same
       work order re-fetched after a save is a different revision, and that is
       exactly when the local copy should be replaced. */
    useEffect(() => {
        setAssigneeId(workOrder.assigneeId ?? '');
        setAdditionalAssigneeIds(readAdditionalAssigneeIds(workOrder));
        setAssigneeHours(readAssigneeHours(workOrder));
        setNotes(workOrder.checklistNotes ?? '');
        setError(null);
    }, [workOrder.id, workOrder.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

    /* An hour-tracked job (Require Hours ticked, or a T&M sub-type forcing it
       on) swaps the plain crew list for the hours editor — see the docblock. */
    const tracksHours = Boolean(workOrder.requiresHours);
    const isTimeAndMaterials = workOrder.subType === 't_and_m';
    const bucketHours = workOrder.allocatedHours != null
        ? Number(workOrder.allocatedHours)
        : undefined;

    /* Any local edit clears the "Saved" confirmation — it described the
       previous state and would otherwise claim unsaved work is safe. */
    const markDirty = useCallback(() => setSaved(false), []);

    const handleAssigneeChange = useCallback((id: string) => {
        markDirty();
        setAssigneeId(id);
    }, [markDirty]);

    const handleAdditionalChange = useCallback((ids: string[]) => {
        markDirty();
        setAdditionalAssigneeIds(ids);
    }, [markDirty]);

    const handleHoursChange = useCallback((hours: Record<string, string>) => {
        markDirty();
        setAssigneeHours(hours);
    }, [markDirty]);

    const handleNotesChange = useCallback((next: string) => {
        markDirty();
        setNotes(next);
    }, [markDirty]);

    /** Directions to the site, built from all five address columns so the
     *  maps app gets the whole address rather than just the street line.
     *  Null when nothing is on file — the link is gated on it. */
    const directionsUrl = useMemo(() => getDirectionsUrlFromParts({
        address: workOrder.projectAddress,
        addressLine2: workOrder.projectAddressLine2,
        city: workOrder.projectCity,
        state: workOrder.projectState,
        zip: workOrder.projectZip,
    }), [
        workOrder.projectAddress,
        workOrder.projectAddressLine2,
        workOrder.projectCity,
        workOrder.projectState,
        workOrder.projectZip,
    ]);

    /** The site address on one line, for display under the Directions link. */
    const displayAddress = useMemo(() => [
        workOrder.projectAddress,
        workOrder.projectAddressLine2,
        [workOrder.projectCity, workOrder.projectState].filter(Boolean).join(', '),
        workOrder.projectZip,
    ].filter(Boolean).join(' · '), [
        workOrder.projectAddress,
        workOrder.projectAddressLine2,
        workOrder.projectCity,
        workOrder.projectState,
        workOrder.projectZip,
    ]);

    /* The primary assignee's display name, for the read-only row above the
       crew list. Falls back to the eager-loaded relation when the flattened
       name is absent. */
    const primaryName = workOrder.assigneeName
        || (workOrder.assignees ?? [])
            .filter((a) => Number(a.pivot?.isPrimary ?? 0))
            .map((a) => `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim())
            .find(Boolean)
        || null;

    /* What the tech has actually changed. Tracked separately for the crew and
       the notes because the server gates the two differently: touching the
       crew on an hour-tracked job means every person on it must carry hours
       (assertHoursSupplied), and a tech who only came here to write a note
       should never be stopped by that rule. So the save sends the crew keys
       only when the crew moved. */
    const crewDirty = assigneeId !== storedAssigneeId
        || !sameIdSet(additionalAssigneeIds, storedAdditionalIds)
        || (tracksHours && !sameHours(assigneeHours, storedHours));
    const notesDirty = notes !== storedNotes;
    const isDirty = crewDirty || notesDirty;

    /**
     * Save whatever changed in a single PATCH.
     *
     * additional_assignee_ids goes out whole (even empty) when the crew is
     * dirty, because the server syncs the junction table off it — sending a
     * subset would drop the people it left out. Hours ride along on
     * hour-tracked jobs, since the server refuses a crew change without them.
     */
    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            const payload: UpdateWorkOrderData = {};

            if (notesDirty) {
                /* Empty box means "no notes", not an empty string, so the
                   column reads the same as a job nobody has written on. */
                payload.checklistNotes = notes.trim() ? notes : null;
            }

            if (crewDirty) {
                payload.additionalAssigneeIds = additionalAssigneeIds;

                /* Only send a primary when one is set — the hours editor can
                   leave the seat empty while the crew is being rearranged,
                   and posting an empty assignee_id would fail the exists: rule. */
                if (assigneeId) payload.assigneeId = assigneeId;

                if (tracksHours) {
                    const entries = Object.entries(assigneeHours).filter(([, v]) => v !== '' && v != null);
                    payload.assigneeHours = Object.fromEntries(
                        entries.map(([uid, v]) => [uid, Number(v)]),
                    );
                }
            }

            await workOrderService.update(workOrder.id, payload);
            setSaved(true);
            onSaved?.();
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not save your changes. Check your connection and try again.'));
        } finally {
            setSaving(false);
        }
    }, [
        workOrder.id,
        crewDirty,
        notesDirty,
        additionalAssigneeIds,
        assigneeId,
        assigneeHours,
        notes,
        tracksHours,
        onSaved,
    ]);

    return (
        <div
            className={['workflow-job-details', 'absolute inset-0 z-10 flex flex-col bg-brand-surface', className ?? ''].join(' ')}
            role="dialog"
            aria-modal="true"
            aria-label="Job details"
            data-ref="organism-workflow-job-details"
        >
            {/* --- Panel header --- */}
            <div className="workflow-job-details__header flex-shrink-0 flex items-center gap-8px px-16px py-12px bg-white border-b border-gray-200">
                <button
                    type="button"
                    onClick={onClose}
                    className="w-44px h-44px -ml-8px rounded-full flex items-center justify-center text-brand-text-muted hover:text-brand-text hover:bg-gray-100 transition-colors"
                    aria-label="Back to checklist"
                >
                    <svg className="w-24px h-24px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                    </svg>
                </button>
                <h2 className="text-16px font-sans font-bold text-brand-text flex-1 truncate">Job Details</h2>
            </div>

            {/* --- Scrollable body --- */}
            <div className="workflow-job-details__body flex-1 overflow-y-auto px-16px py-16px space-y-16px">
                {/* ============================================
                  * 1. PEOPLE — who is on the job
                  * ============================================ */}
                <SectionCard title="People On This Job" className="workflow-job-details__people" dataRef="section-people">
                    {/* On an hour-tracked job the hours editor OWNS the primary
                        row (it needs an hours input against it), so the
                        read-only readout would duplicate it. */}
                    {!tracksHours && (
                        <p className="text-13px font-sans text-brand-text-muted mb-12px">
                            Assigned to{' '}
                            <span className="font-semibold text-brand-text">{primaryName ?? 'nobody yet'}</span>
                            . Add anyone else working this job with you.
                        </p>
                    )}

                    {tracksHours ? (
                        <TmAssigneeHoursFieldset
                            assigneeId={assigneeId}
                            onAssigneeChange={handleAssigneeChange}
                            additionalAssigneeIds={additionalAssigneeIds}
                            onAdditionalAssigneesChange={handleAdditionalChange}
                            assigneeHours={assigneeHours}
                            onAssigneeHoursChange={handleHoursChange}
                            bucketHours={bucketHours}
                            isTimeAndMaterials={isTimeAndMaterials}
                            /* Quick-add anyone missing from the crew; they
                               default to this work order's business and
                               arrive as rows waiting for hours. */
                            quickAdd={{ companyId: workOrder.companyId }}
                            primaryLocked={!user?.canReassign}
                            labelClass={LABEL_CLASS}
                        />
                    ) : (
                        /* showPrimary={false} — a tech on site adds helpers;
                           who OWNS the job stays a dispatch decision. */
                        <AssigneeFieldset
                            formType="work_order"
                            assigneeId={assigneeId}
                            onAssigneeChange={handleAssigneeChange}
                            additionalAssigneeIds={additionalAssigneeIds}
                            onAdditionalAssigneesChange={handleAdditionalChange}
                            showPrimary={false}
                            additionalLabel="Also On Site"
                            additionalHint="Everyone added here can work this checklist and shows on the job record."
                            /* Someone on site who isn't in the system yet. */
                            quickAdd={{ companyId: workOrder.companyId }}
                            labelClass={LABEL_CLASS}
                        />
                    )}
                </SectionCard>

                {/* ============================================
                  * 2. NOTES — what happened on site
                  * ============================================ */}
                <SectionCard title="Job Notes" className="workflow-job-details__notes" dataRef="section-notes">
                    <p className="text-13px font-sans text-brand-text-muted mb-12px -mt-8px">
                        Anything the office should know — access problems, extra work, damage found.
                        These travel with the work order and appear on sign-off.
                    </p>
                    <AutoGrowTextarea
                        value={notes}
                        onChange={handleNotesChange}
                        placeholder="Add a note about this job..."
                        minRows={4}
                    />
                </SectionCard>

                {/* ============================================
                  * 3. JOB INFO — read-only reference
                  * ============================================ */}
                {/* Same card chrome as the two above, with InfoSection
                    supplying the (identically styled) heading and the dl. */}
                <SectionCard className="workflow-job-details__info" dataRef="section-info">
                    <InfoSection heading="Job Info">
                        <InfoRow label="Work Order" value={workOrder.projectName} />
                        <InfoRow label="Customer" value={workOrder.companyName} />
                        <InfoRow label="Location" value={workOrder.locationName} />

                        {/* Address doubles as a Directions launcher — the single
                            most-used thing on a phone parked outside the site. */}
                        <InfoRow label="Site Address">
                            {displayAddress ? (
                                <div className="flex flex-col gap-4px">
                                    <span>{displayAddress}</span>
                                    {directionsUrl && (
                                        <a
                                            href={directionsUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-4px min-h-44px text-14px font-sans font-semibold text-brand-primary hover:underline"
                                        >
                                            <svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                            </svg>
                                            Directions
                                        </a>
                                    )}
                                </div>
                            ) : undefined}
                        </InfoRow>

                        {/* Contact details are tap targets, not text — a tech
                            reading this is standing outside a locked door. */}
                        <InfoRow label="On-Site Contact">
                            {workOrder.contactName || workOrder.contactPhone || workOrder.contactEmail ? (
                                <div className="flex flex-col gap-4px">
                                    {workOrder.contactName && <span>{workOrder.contactName}</span>}
                                    {workOrder.contactPhone && (
                                        <a href={`tel:${workOrder.contactPhone}`} className="inline-flex items-center min-h-44px text-brand-primary font-semibold hover:underline">
                                            {formatPhone(workOrder.contactPhone)}
                                        </a>
                                    )}
                                    {workOrder.contactEmail && (
                                        <a href={`mailto:${workOrder.contactEmail}`} className="inline-flex items-center min-h-44px text-brand-primary font-semibold hover:underline break-all">
                                            {workOrder.contactEmail}
                                        </a>
                                    )}
                                </div>
                            ) : undefined}
                        </InfoRow>

                        <InfoRow label="Scheduled" value={workOrder.scheduledDate ? formatDate(workOrder.scheduledDate) : null} />
                        {(workOrder.startTime || workOrder.endTime) && (
                            <InfoRow label="Window">
                                {[
                                    workOrder.startTime ? formatDateTime(workOrder.startTime) : null,
                                    workOrder.endTime ? formatDateTime(workOrder.endTime) : null,
                                ].filter(Boolean).join(' – ')}
                            </InfoRow>
                        )}
                        <InfoRow label="Priority" value={formatLabel(workOrder.priority)} />
                        <InfoRow label="Service" value={[
                            formatLabel(workOrder.businessLine),
                            workOrder.subType ? formatLabel(workOrder.subType) : null,
                        ].filter(Boolean).join(' · ')} />
                        {tracksHours && (
                            <InfoRow
                                label="Hour Bucket"
                                value={bucketHours != null ? `${bucketHours} hrs` : null}
                                emptyText="No bucket set"
                            />
                        )}
                        <InfoRow label="Description" value={workOrder.description} preserveNewlines />
                    </InfoSection>
                </SectionCard>
            </div>

            {/* --- Save bar --- */}
            <div className="workflow-job-details__footer flex-shrink-0 bg-white border-t border-gray-200 px-16px py-12px">
                {error && <Alert variant="error" size="sm" icon className="mb-8px">{error}</Alert>}
                {saved && !error && (
                    <Alert variant="success" size="sm" icon className="mb-8px">Job details saved.</Alert>
                )}
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !isDirty}
                    className="w-full inline-flex items-center justify-center px-24px py-12px text-16px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg min-h-48px transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving…' : 'Save Job Details'}
                </button>
            </div>
        </div>
    );
};

export default WorkflowJobDetails;
